import React from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import {
  getComponentWeightInAmount,
  getComponentWeightInAmountUnit,
} from '../../utils/componentWeightDisplay';

const DishesIngredientsInformation = ({ dish }) => {
    const { t } = useTranslation();
    
    // Comprehensive data validation and extraction
    const ingredients = Array.isArray(dish?.ingredients) ? dish.ingredients : [];
    const originalContents = dish?.original_contents || '';
    const description = dish?.description || '';

    // Robust validation - check if we have any meaningful data
    const hasIngredientsTable = ingredients.length > 0 && ingredients.some(ing => 
        ing && (ing.name || ing.id)
    );
    const hasIngredientsString = originalContents && originalContents.trim().length > 0;
    const hasDescription = description && description.trim().length > 0;

    // Don't render if no dish or no meaningful data
    if (!dish || (!hasIngredientsTable && !hasIngredientsString && !hasDescription)) {
        return null;
    }

    // Robust helper function to get ingredient name
    const getIngredientName = (ingredient) => {
        if (!ingredient) return 'N/A';
        return ingredient.name || ingredient.title || ingredient.label || 'N/A';
    };

    const stripPrefix = (text) => {
        if (!text || typeof text !== "string") return "";
        return text
            .replace(/^\s*Ingredienser\s*:\s*/iu, "")
            .replace(/\s+/g, " ")
            .trim();
    };

    // Helper function to format original amount and unit
    const getOriginalAmountAndUnit = (ingredient) => {
        if (!ingredient) return 'N/A';
        
        const insertedValue = ingredient.inserted_value;
        const chosenUnit = ingredient.chosen_unit;
        
        if (insertedValue && chosenUnit) {
            // Keep backend unit key "styck", but present it as "portion" in PDF.
            if (chosenUnit === 'styck') {
                return `1 ${t('portion')}`;
            }
            // Remove unnecessary decimal places (e.g., 3.000 -> 3)
            const cleanValue = parseFloat(insertedValue).toString();
            return `${cleanValue}${chosenUnit}`;
        }
        
        return 'N/A';
    };

    // Helper function to format converted amount and unit
    const getConvertedAmountAndUnit = (ingredient) => {
        if (!ingredient) return 'N/A';

        const amount = getComponentWeightInAmount(ingredient);
        const unit = getComponentWeightInAmountUnit();

        if (amount != null) {
            const rounded = Math.round(amount * 10) / 10;
            return `${Math.round(rounded)}${unit}`;
        }

        return 'N/A';
    };

    // Generate ingredients string from ingredients array, sorted by percentage descending
    const generateIngredientsString = () => {
        if (!ingredients || ingredients.length === 0) {
            return originalContents || '';
        }

        const formatPct = (value) => {
            const num = parseFloat(value);
            if (!Number.isFinite(num)) return null;
            const rounded = parseFloat(num.toFixed(1));
            if (!Number.isFinite(rounded)) return null;
            return rounded % 1 === 0 ? `${rounded.toFixed(0)}%` : `${rounded.toFixed(1)}%`;
        };

        // Sort ingredients by percentage in descending order
        const sortedIngredients = [...ingredients]
            .filter((ing) => ing && (ing.name || ing.id))
            .sort((a, b) => {
                const getPercentage = (ing) => {
                    const pct = ing.percentage ?? ing.weight_in_percentage ?? ing.inserted_percentage;
                    const num = parseFloat(pct);
                    return Number.isFinite(num) ? num : 0;
                };
                return getPercentage(b) - getPercentage(a);
            });

        const parts = sortedIngredients
            .map((component) => {
                const pctStr = formatPct(
                    component.percentage ?? component.weight_in_percentage ?? component.inserted_percentage
                );

                const applyPercentage = (label) => {
                    if (!label) return "";
                    return pctStr ? `${label} (${pctStr})` : label;
                };

                const baseName =
                    component.name ||
                    component.product_name ||
                    component.dish_name ||
                    component.title ||
                    component.label ||
                    "";

                if (
                    component.type === "ingredient" ||
                    component.ingredient_id ||
                    component.sourceType === "ingredient" ||
                    component.source_type === "ingredient"
                ) {
                    return applyPercentage(baseName);
                }

                if (
                    component.type === "product" ||
                    component.product_id ||
                    component.sourceType === "product" ||
                    component.source_type === "product"
                ) {
                    const productName = applyPercentage(baseName);
                    const productContents = stripPrefix(
                        component.original_contents ||
                        component.product_original_contents ||
                        component.contents ||
                        ""
                    );
                    if (productContents) {
                        return productName ? `${productName}: ${productContents}` : productContents;
                    }
                    return productName;
                }

                if (
                    component.type === "recipe" ||
                    component.child_dish_id ||
                    component.sourceType === "recipe" ||
                    component.source_type === "recipe"
                ) {
                    const childContents = stripPrefix(
                        component.original_contents ||
                        component.child_original_contents ||
                        component.child_contents ||
                        ""
                    );
                    if (childContents) {
                        return childContents;
                    }
                    return applyPercentage(baseName);
                }

                return applyPercentage(baseName);
            })
            .filter((part) => part && part.length > 0);

        if (parts.length > 0) {
            return `Ingredienser: ${parts.join(', ')}`;
        }

        return originalContents || '';
    };

    const ingredientsString = hasIngredientsTable ? generateIngredientsString() : originalContents;

    return (
        <div className="px-4 print:break-before-page" style={{ pageBreakBefore: 'always', pageBreakAfter: 'always' }}>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Ingredients Table */}
                {hasIngredientsTable && (
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden print:bg-white">
                        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 print:bg-gray-50">
                            <h3 className="text-md font-medium text-gray-800">
                                {t("Ingredienser")}
                            </h3>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 print:bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t("Ingrediens")}
                                        </th>
                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t("Enhet")}
                                        </th>
                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 lowercase tracking-wider">
                                            {t("g/ml")}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200 print:bg-white">
                                    {ingredients
                                        .filter(ing => ing && (ing.name || ing.id))
                                        .sort((a, b) => {
                                            const getPercentage = (ing) => {
                                                const pct = ing.percentage ?? ing.weight_in_percentage ?? ing.inserted_percentage;
                                                const num = parseFloat(pct);
                                                return Number.isFinite(num) ? num : 0;
                                            };
                                            return getPercentage(b) - getPercentage(a);
                                        })
                                        .map((ingredient, index) => (
                                        <tr key={ingredient.id || ingredient.name || index} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 text-sm text-gray-900">
                                                {getIngredientName(ingredient)}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900 text-center">
                                                {getOriginalAmountAndUnit(ingredient)}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900 text-center">
                                                {getConvertedAmountAndUnit(ingredient)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Ingredients String */}
                {hasIngredientsString && (
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden print:bg-white">
                        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 print:bg-gray-50">
                            <h3 className="text-md font-medium text-gray-800">
                                {t("Innehållsförteckning")}
                            </h3>
                        </div>
                        <div className="p-4">
                            <p className="whitespace-pre-wrap leading-relaxed text-gray-800">
                                {ingredientsString}
                            </p>
                        </div>
                    </div>
                )}

                {/* Description */}
                {hasDescription && (
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden print:bg-white">
                        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 print:bg-gray-50">
                            <h3 className="text-md font-medium text-gray-800">
                                {t("Gör så här")}
                            </h3>
                        </div>
                        <div className="p-4">
                            <p className="whitespace-pre-wrap leading-relaxed text-gray-800">
                                {description}
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Show message if no data available */}
            {!hasIngredientsTable && !hasIngredientsString && !hasDescription && (
                <div className="text-center py-8 text-gray-500">
                    <p>{t("Ingen information tillgänglig")}</p>
                </div>
            )}
        </div>
    );
};

export default DishesIngredientsInformation;